import React from 'react';
import { theme } from '../theme';

// ─── ActionButton ────────────────────────────────────────
// The bottom-bar button of a ticket detail — the house grammar for a decision the clerk takes on
// the ticket in front of them: FILLED navy = a live action (every clickable button in either bar
// wears it since 2026-09-08, Archivieren included), OUTLINED = the `off` face alone, and a
// disabled button says WHY in its title rather than vanishing.
// Lived in TicketDetail until 2026-09-07, when the triage bottom bar (now PressBar) became the
// second surface built from it — two surfaces, one geometry, so a padding or a border weight can
// never drift between the letter flow's bar and the triage flow's.
// justifyContent centres the label when a caller STRETCHES the button to a pre-allocated slot
// width (the triage bar does); at the natural, content-hugging width it changes nothing.
// `face` (2026-09-08, operator ruling — the triage bar's three-face grammar). An OPT-IN prop: a
// caller that does not pass it gets the filled button, dimmed to 0.6 when disabled. When it IS
// passed, the three faces answer one question — can the clerk get something out of this button?
// The caller-supplied `outlined` escape hatch was DELETED in review round 1 (2026-09-08): the navy
// ruling left it with zero call sites, and the one look it produced is what `off` means now.
//   'on'        — the caller's ordinary filled face, live.
//   'potential' — FILLED and disabled only because the thing it acts on does not exist yet: the
//                 clerk can produce it, and then this same button turns on. Dimmed HARDER than the
//                 ordinary disabled 0.6, because the dimming is the whole message.
//   'off'       — PLAIN white outlined at FULL opacity: navy border, navy text, no wash. Not
//                 clickable, and rendering something will not change that.
// ⚠ `off` deliberately does NOT take the shared disabled 0.6. Dimming is what separates
// `potential` from `on`; a washed-out white would blur the three faces into two-and-a-half, which
// is the exact look this ruling removes. The grammar is: only clickable = full navy,
// could-be-clickable = washed navy, not clickable = full white. `cursor: not-allowed` and the
// German `title` are what say "disabled" on that face, not opacity.
export type ActionButtonFace = 'on' | 'potential' | 'off';

export const ActionButton: React.FC<{
  label: string; onClick: () => void; disabled?: boolean; bg?: string;
  face?: ActionButtonFace; icon?: React.ReactNode; title?: string;
  /**
   * Disabled, but NOT YET visibly so (walk fix round 4, 2026-09-09). A draft action in flight
   * refuses every other draft slot immediately — that half must never be debounced, or a
   * double-submit gets through — while the DIM waits ~250ms, because on a fast round trip the
   * whole bar washed out and back in inside a couple of frames and read as a glitch rather than
   * as a state. This prop is that window and nothing else: the press is already refused.
   * Deliberately narrow: it never applies to a slot disabled for its own reason (a kind this row
   * cannot offer, maintenance) — those are not transients and must look disabled at once.
   */
  undimmed?: boolean;
  /**
   * Which of two LIVE buttons the row recommends (part 5-2, board 3b): `primary` (default) is the
   * filled face; `secondary` is the outline face — white ground, 1.5px navy ring, navy text — for
   * the alternative beside a recommended act. A weight, not a state: availability still comes from
   * `face`/`disabled` exactly as before, and `off` outranks it (an unavailable slot keeps its own
   * outlined face). The ring is an INSET shadow, not a border, so the box is the filled button's
   * box to the pixel — switching weight moves nothing.
   */
  emphasis?: 'primary' | 'secondary';
  /** Fill its container (a PressBar third): full width, the label ending in an ellipsis when the
   *  third is narrower than the verb. */
  fill?: boolean;
}> = ({ label, onClick, disabled, bg, face, icon, title, undimmed, emphasis = 'primary', fill }) => {
  const isOutlined = face === 'off';
  const isQuiet = !isOutlined && emphasis === 'secondary';
  // Precedence, and it matters: 'off' claims full opacity BEFORE the shared disabled fade can
  // apply to it, 'undimmed' then claims it for the pre-dim window of a transient hold, and 'on'
  // otherwise falls through to that fade so an in-flight press still reads as busy.
  const opacity = face === 'potential' ? 0.45
    : face === 'off' ? 1
      : undimmed ? 1
        : disabled ? 0.6 : 1;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
        fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
        color: isOutlined || isQuiet ? theme.colors.navy : theme.colors.white,
        backgroundColor: isOutlined || isQuiet ? theme.colors.white : bg || theme.colors.navy,
        border: isOutlined ? `2px solid ${theme.colors.navy}` : 'none',
        boxShadow: isQuiet ? `inset 0 0 0 1.5px ${theme.colors.navy}` : undefined,
        borderRadius: theme.borderRadius.md,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity,
        // Never wraps: a verb broken onto two lines is a shorter button, and the slots beside it move.
        whiteSpace: 'nowrap',
        transition: 'opacity 0.15s ease, background-color 0.15s ease',
        ...(fill ? { width: '100%', minWidth: 0, height: 40, padding: '0 8px', boxSizing: 'border-box' as const } : {}),
      }}
    >
      {icon}{fill ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{label}</span> : label}
    </button>
  );
};
